import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Show, UserButton, useAuth, useClerk, ClerkLoading, ClerkFailed } from "@clerk/react";
import axios from "axios";
import { Radio, MessagesSquare, MessageCircle, Paperclip, Users, Flag, ShieldBan } from "lucide-react";
import { Logo } from "@/components/Logo";
import { AuthLoading, AuthFailed } from "@/components/auth/AuthStatus";
import { API } from "@/lib/api";
import { ConversationsPanel } from "@/components/admin/ConversationsPanel";
import { UsersPanel } from "@/components/admin/UsersPanel";
import { ReportsPanel } from "@/components/admin/ReportsPanel";
import { BansPanel } from "@/components/admin/BansPanel";
import { AnalyticsPanel } from "@/components/admin/AnalyticsPanel";

const TABS = [["conv", "Beszélgetések"], ["stats", "Nézettség"], ["reports", "Jelentések"], ["bans", "Kitiltások"], ["users", "Admin fiókok"]];

export const useAuthedGet = () => {
  const { getToken } = useAuth();
  return async (url, opts = {}) => {
    const token = await getToken();
    return axios({ url: `${API}${url}`, method: "get", ...opts, headers: { Authorization: `Bearer ${token}` } });
  };
};

const Stat = ({ icon: I, label, value, color, testId }) => (
  <div className="rounded-3xl border border-white/10 bg-[#1A152E] p-5" data-testid={testId}>
    <I className="h-5 w-5" style={{ color }} />
    <p className="mt-4 font-display text-3xl font-black">{value ?? "…"}</p>
    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
  </div>
);

const Dashboard = () => {
  const req = useAuthedGet();
  const { signOut } = useClerk();
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("conv");

  useEffect(() => {
    req("/me").then((r) => setMe(r.data)).catch((e) => setMe({ role: e.response?.status === 403 ? "member" : "error" }));
  }, []); // eslint-disable-line
  useEffect(() => {
    if (me?.role !== "admin") return;
    const load = () => req("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [me]); // eslint-disable-line

  if (!me) return <AuthLoading />;
  if (me.role === "error")
    return (
      <div className="mx-auto max-w-md p-10 text-center" data-testid="admin-error">
        <h2 className="font-display text-2xl font-bold">Nem sikerült betölteni az admin adatokat</h2>
        <p className="mt-2 text-sm text-muted-foreground">Frissítsd az oldalt. Ha a hiba megmarad, jelentkezz ki és be újra.</p>
        <button onClick={() => window.location.reload()} data-testid="admin-retry-button"
          className="mt-5 rounded-full bg-[#FF3B6C] px-5 py-2 font-bold text-white transition-transform hover:scale-105">Újratöltés</button>
      </div>
    );
  if (me.role !== "admin")
    return (
      <div className="mx-auto max-w-md p-10 text-center" data-testid="admin-forbidden">
        <h2 className="font-display text-2xl font-bold">Nincs jogosultságod</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Most ezzel a fiókkal vagy belépve: <b className="text-white">{me.email || "ismeretlen fiók"}</b>. Az admin felülethez az admin e-mail címmel kell belépned.
        </p>
        <button onClick={() => signOut({ redirectUrl: "/sign-in" })} data-testid="admin-switch-account-button"
          className="mt-5 rounded-full bg-[#FF3B6C] px-5 py-2 font-bold text-white transition-transform hover:scale-105">Belépés másik fiókkal</button>
      </div>
    );

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-8">
      <h1 className="font-display text-4xl font-black tracking-tight">Admin panel</h1>
      <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-7">
        <Stat testId="stat-online" icon={Users} label="Online" value={stats?.online} color="#00E676" />
        <Stat testId="stat-live" icon={Radio} label="Élő chat" value={stats?.live} color="#FF3B6C" />
        <Stat testId="stat-total" icon={MessagesSquare} label="Összes chat" value={stats?.total} color="#7C3AED" />
        <Stat testId="stat-messages" icon={MessageCircle} label="Üzenet" value={stats?.messages} color="#00F0FF" />
        <Stat testId="stat-files" icon={Paperclip} label="Fájl" value={stats?.files} color="#FFD600" />
        <Stat testId="stat-open-reports" icon={Flag} label="Nyitott jelentés" value={stats?.open_reports} color="#FF3B6C" />
        <Stat testId="stat-active-bans" icon={ShieldBan} label="Aktív tiltás" value={stats?.active_bans} color="#EF4444" />
      </div>
      <div className="mt-10 flex flex-wrap gap-2">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
            className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold transition-colors ${tab === k ? "bg-[#FF3B6C] text-white" : "bg-white/5 text-muted-foreground hover:bg-white/10"}`}>
            {l}
            {k === "reports" && stats?.open_reports > 0 && <span className="rounded-full bg-[#FFD600] px-2 text-xs text-[#0F0C1B]" data-testid="reports-badge">{stats.open_reports}</span>}
          </button>
        ))}
      </div>
      <div className="mt-6">
        {tab === "conv" && <ConversationsPanel />}
        {tab === "stats" && <AnalyticsPanel />}
        {tab === "reports" && <ReportsPanel />}
        {tab === "bans" && <BansPanel />}
        {tab === "users" && <UsersPanel />}
      </div>
    </div>
  );
};

export default function Admin() {
  return (
    <div className="grain min-h-screen">
      <header className="relative z-10 flex items-center justify-between border-b border-white/10 px-4 py-4 md:px-8">
        <Logo size="text-xl" />
        <Show when="signed-in"><UserButton /></Show>
      </header>
      <div className="relative z-10">
        <ClerkLoading><AuthLoading /></ClerkLoading>
        <ClerkFailed><AuthFailed /></ClerkFailed>
        <Show when="signed-in"><Dashboard /></Show>
        <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
      </div>
    </div>
  );
}
